import React, { useState, useEffect } from 'react';
import { CheckCircle, Server, Zap, Shield, Monitor, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { getStorageUrl } from '../utils/imageUrlUpdater';

const VPSWindows: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentGallerySlide, setCurrentGallerySlide] = useState(0);

  // Set page title and canonical URL
  useEffect(() => {
    document.title = 'VPS Windows - VMon Cloud';
    
    // Set canonical URL
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }
    
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = 'https://vmon.vn/vps-windows';
    document.head.appendChild(canonical);
    
    return () => {
      if (canonical.parentNode) {
        canonical.parentNode.removeChild(canonical);
      }
      document.title = 'VMon Cloud - Dịch vụ VPS và Hosting chất lượng cao';
    };
  }, []);

  const plans = [
    {
      name: 'Windows - 1 GB',
      price: '115,000',
      originalPrice: '150,000',
      cpu: '1 VCpu',
      ram: '1GB',
      storage: '30GB SSD NVMe',
      bandwidth: 'Không giới hạn',
      features: ['1Gbps Ethernet Port', 'Root Access', 'Free Backup', '99.9% Uptime'],
      popular: false
    },
    {
      name: 'Windows - 2 GB',
      price: '155,000',
      originalPrice: '200,000',
      cpu: '1 VCpu',
      ram: '2GB',
      storage: '30GB SSD NVMe',
      bandwidth: 'Không giới hạn',
      features: ['1Gbps Ethernet Port', 'Root Access', 'Free Backup', '99.9% Uptime'],
      popular: false
    },
    {
      name: 'Windows - 2 GB - 2 CPU',
      price: '175,000',
      originalPrice: '250,000',
      cpu: '2 VCpu',
      ram: '2GB',
      storage: '30GB SSD NVMe',
      bandwidth: 'Không giới hạn',
      features: ['1Gbps Ethernet Port', 'Root Access', 'Free Backup', '99.9% Uptime'],
      popular: true
    },
    {
      name: 'Windows - 4 GB',
      price: '315,000',
      originalPrice: '415,000',
      cpu: '2 VCpu',
      ram: '4GB',
      storage: '60GB SSD NVMe',
      bandwidth: 'Không giới hạn',
      features: ['1Gbps Ethernet Port', 'Root Access', 'Free Backup', '99.9% Uptime'],
      popular: true
    },
    {
      name: 'Windows - 8 GB',
      price: '575,000',
      originalPrice: '800,000',
      cpu: '4 VCpu',
      ram: '8GB',
      storage: '100GB SSD NVMe',
      bandwidth: 'Không giới hạn',
      features: ['1Gbps Ethernet Port', 'Root Access', 'Free Backup', '99.9% Uptime'],
      popular: false
    },
    {
      name: 'Windows - 16 GB',
      price: '915,000',
      originalPrice: '1,500,000',
      cpu: '8 VCpu',
      ram: '16GB',
      storage: '180GB SSD NVMe',
      bandwidth: 'Không giới hạn',
      features: ['1Gbps Ethernet Port', 'Root Access', 'Free Backup', '99.9% Uptime'],
      popular: false
    }
  ];

  const specifications = [
    { label: 'Operating System', value: 'Windows Server 2019/2022' },
    { label: 'Remote Access', value: 'RDP (Remote Desktop)' },
    { label: 'Web Server', value: 'IIS 10.0' },
    { label: 'Database', value: 'SQL Server Express/Standard' },
    { label: 'Control Panel', value: 'Windows Admin Center' },
    { label: 'Setup Time', value: 'Instant' },
    { label: 'License', value: 'Included' },
    { label: 'Support', value: '24/7 Technical Support' }
  ];

  const additionalPricing = [
    { item: '1 IP', price: '50,000 VNĐ / Tháng' },
    { item: '1 VCpu', price: '50,000 VNĐ / Tháng' },
    { item: '1 GB Ram', price: '50,000 VNĐ / Tháng' },
    { item: '10GB ổ cứng SSD NVMe', price: '30,000 VNĐ / Tháng' }
  ];

  const advantages = [
    {
      icon: Server,
      title: 'HỖ TRỢ CÁC ỨNG DỤNG WINDOWS',
      description: 'VPS Windows hỗ trợ các ứng dụng phần mềm Windows và là giải pháp tuyệt vời để chạy các ứng dụng này từ bất kỳ đâu. Với tính bảo mật cao và quản lý dữ liệu tốt hơn, VPS Windows là lựa chọn đáng tin cậy cho việc sử dụng các ứng dụng Windows.'
    },
    {
      icon: Zap,
      title: 'DỄ SỬ DỤNG',
      description: 'Giao diện của VPS Windows quen thuộc và dễ sử dụng, thích hợp cho những ai mới bắt đầu làm quen với VPS. Bạn có thể sử dụng VPS Windows như sử dụng chiếc máy tính của mình một cách đơn giản và thuận tiện.'
    },
    {
      icon: Shield,
      title: 'KHÔNG GIỚI HẠN BĂNG THÔNG',
      description: 'VPS Windows tại VMon Cloud cung cấp băng thông lên tới 1Gbps và không giới hạn Data Transfer. Điều này giúp tối ưu hiệu suất của VPS và không bị giới hạn bởi lượng dữ liệu trao đổi trong tháng. Bạn có thể sử dụng VPS Windows để hoạt động đạt hiệu suất cao và ổn định hơn.'
    },
    {
      icon: Monitor,
      title: 'HỆ ĐIỀU HÀNH ĐA DẠNG',
      description: 'VPS Windows là một giải pháp ấn tượng của điện toán đám mây, với nhiều phiên bản hệ điều hành, bao gồm Windows Server 2012R2, Windows Server 2019, Windows Server 2022, Windows 10,... Với khả năng tối ưu điều khiển và sự thuận tiện khi sử dụng giao diện Windows quen thuộc, VPS Windows được xem là lựa chọn lý tưởng cho người dùng.'
    }
  ];

  const benefits = [
    {
      icon: Server,
      title: 'Hạ tầng mạnh mẽ',
      description: 'Hệ thống VPS Windows tại VMon Cloud được thiết kế dự phòng nhiều lớp, sử dụng phần cứng tối ưu và tính năng giám sát cảnh báo toàn diện, giúp bảo đảm hoạt động ổn định và hỗ trợ người dùng trong mọi tình huống.'
    },
    {
      icon: Shield,
      title: 'HOÀN TIỀN NẾU KHÔNG HÀI LÒNG',
      description: 'Với VPS Windows của VMon Cloud, bạn được bảo đảm tính năng và chất lượng dịch vụ hàng đầu. Nếu trong 7 ngày đầu sử dụng, bạn không hài lòng với sản phẩm của chúng tôi vì bất kỳ lý do gì, VMon Cloud sẽ hoàn tiền 100%, đảm bảo chất lượng dịch vụ và sự hài lòng của khách hàng.'
    },
    {
      icon: Zap,
      title: 'DỄ DÀNG QUẢN TRỊ VÀ NÂNG CẤP',
      description: 'Với VPS Windows của VMon Cloud, bạn sẽ được truy cập vào Trang quản lý dễ sử dụng của chúng tôi, giúp bạn quản lý và nâng cấp cấu hình dễ dàng. Không còn phải lo lắng về việc quản lý VPS, bạn có thể tập trung vào công việc kinh doanh của mình mà không gián đoạn.'
    },
    {
      icon: Globe,
      title: 'Kênh hỗ trợ 24/7',
      description: 'Liên hệ với VMon Cloud để được hỗ trợ và giải đáp thắc mắc qua các kênh chat như Facebook, Zalo, Telegram, Skype hoặc hotline 0966.805.315. Chúng tôi sẵn sàng hỗ trợ bạn 24/7 và giải quyết mọi vấn đề của bạn nhanh chóng và chuyên nghiệp.'
    }
  ];

  const faqItems = [
    {
      question: 'VPS Windows dùng để làm gì?',
      answer: 'VPS Windows được sử dụng với nhiều mục đích khác nhau, có thể kể đến như: 1. Lưu trữ, chia sẻ dữ liệu: VPS Windows cũng có thể được sử dụng để lưu trữ và chia sẻ dữ liệu trực tuyến, đảm bảo tính bảo mật và an toàn cho dữ liệu của người dùng. 2. Phát triển, triển khai các ứng dụng và phần mềm trên nền tảng Windows. 3. Chạy các ứng dụng và phần mềm yêu cầu hệ điều hành Windows: bao gồm các ứng dụng văn phòng, các trò chơi, các phần mềm giải trí, v.v.'
    },
    {
      question: 'Tôi lần đầu sử dụng VPS Windows tôi phải làm gì?',
      answer: 'Nếu bạn mới bắt đầu sử dụng VPS và chưa quen thuộc với công nghệ này, đừng lo lắng. Bài viết "Hướng Dẫn Sử Dụng VPS Windows Với Remote Desktop Chi Tiết Nhất" sẽ giúp bạn có được những kiến thức cơ bản để sử dụng VPS một cách dễ dàng và hiệu quả nhất. Trong bài viết này, chúng tôi sẽ hướng dẫn cho bạn từng bước cách sử dụng VPS bằng phần mềm Remote Desktop, từ cách đăng nhập, cài đặt, kết nối đến các tính năng quản lý VPS.'
    },
    {
      question: 'Khi mua VPS Windows tại VMon Cloud rồi, tôi có thể xem thông tin VPS của mình ở đâu?',
      answer: 'Khi bạn đã hoàn tất việc thanh toán hóa đơn khi mua VPS, sau khoảng từ 3 đến 5 phút, hệ thống sẽ tự động gửi thông tin VPS Windows về email mà bạn đã đăng ký với VMon Cloud.'
    },
    {
      question: 'Làm thế nào để thay đổi mật khẩu trên VPS Windows và các bước thực hiện như thế nào?',
      answer: 'Bạn hoàn toàn có thể tự do thay đổi mật khẩu cho VPS Windows của mình. Bạn tham khảo bài viết: "Hướng Dẫn Đổi Mật Khẩu Máy Chủ Ảo VPS Windows" của VMon Cloud để biết chi tiết cách đổi mật khẩu nhé.'
    },
    {
      question: 'Tôi muốn quản lý nhiều VPS?',
      answer: 'Remote Desktop Manager cho phép bạn quản lý nhiều VPS một cách dễ dàng. Nếu bạn quan tâm, hãy tham khảo bài viết hướng dẫn về cách quản lý nhiều VPS với Remote Desktop Connection Manager để tìm hiểu thêm.'
    },
    {
      question: 'VPS Windows IP chung hay riêng?',
      answer: 'Mỗi VPS là 1 IP riêng biệt.'
    },
    {
      question: 'Nên sử dụng VPS Windows 10 không?',
      answer: 'Nên sử dụng khi bạn cần sử dụng phần mềm chỉ hoạt động tốt trên windows 10 và có một cấu hình vps ổn. Đa số các phần mềm windows đều hoạt động được trên các hệ điều hành windows của microsoft, vậy để tối ưu hiệu suất bạn nên sử dụng các hệ điều hành windows server vì khi sử dụng windows 10 có thể có hiện tượng lag nếu bạn không có một vps cấu hình đủ khỏe để chạy.'
    },
    {
      question: 'Có nên sử dụng VPS Windows free không?',
      answer: 'Một số nhà cung cấp đám mây lớn trên thế giới, như AWS, AZURE và Google Cloud, cung cấp khoảng 200 USD miễn phí cho khách hàng sử dụng. Tuy nhiên, khi số tiền miễn phí đã hết, khách hàng sẽ phải trả phí hàng tháng khá đắt đỏ. Vì vậy, nếu bạn có nhu cầu sử dụng dịch vụ đám mây lâu dài và ổn định, hãy cân nhắc trước khi sử dụng các gói miễn phí này.'
    },
    {
      question: 'Các lỗi thường gặp với VPS Windows',
      answer: 'Lỗi CredSSP, vào 2018 Microsoft đã tung ra một bản cập nhập bảo mật dẫn đến một số vps chưa được cập nhập sẽ bị lỗi khi kết nối. Lỗi An internal error has occurred, đây là lỗi khá phổ biến trên vps windows và có rất nhiều lý do gây ra lỗi này dẫn đến không thể kết nối tới vps. Windows tự chuyển sang màn hình đen sau khi update.'
    },
    {
      question: 'Có nhiều hệ điều hành, tôi nên chọn hệ điều hành nào?',
      answer: 'Với tùy cấu hình và mục đích sử dụng khác nhau, bạn nên chọn hệ điều hành phù hợp, bạn có thể tham khảo theo hướng dẫn sau: Với các cấu hình dưới 4GB Ram, bạn nên sử dụng Windows server 2012r2. Với cấu hình trên 4GB Ram, bạn có thể sử dụng Windows server 2019, Windows server 2022, Windows 10.'
    }
  ];

  const testimonials = [
    getStorageUrl('feedback/feedback_1.jpg'),
    getStorageUrl('feedback/feedback_2.jpg'),
    getStorageUrl('feedback/feedback_3.jpg'),
    getStorageUrl('feedback/feedback_4.jpg'),
    getStorageUrl('feedback/feedback_5.jpg'),
    getStorageUrl('feedback/feedback12.jpg'),
    getStorageUrl('feedback/feedback16.jpg'),
    getStorageUrl('feedback/feedback17.jpg')
  ];

  const galleryImages = [
    {
      src: getStorageUrl('panel/Quan-ly-vps.PNG'),
      title: "Quản Lý VPS",
      description: "Quản lý các thông tin vps đầy đủ và tiện lợi."
    },
    {
      src: getStorageUrl('panel/View-VNC.PNG'),
      title: "View VPS qua VNC",
      description: "Truy cập xử lý sự cố vps thông qua VNC."
    },
    {
      src: getStorageUrl('panel/lich-su-so-du.PNG'),
      title: "Số Dư Tài Khoản",
      description: "Việc quản lý số dư tài khoản trở nên thuận tiện và minh bạch hơn."
    },
    {
      src: getStorageUrl('panel/Mua-VPS.PNG'),
      title: "MUA VPS",
      description: "Bạn có thể dễ dàng mua VPS Singapore thông qua giao diện \"one click\"."
    },
    {
      src: getStorageUrl('panel/giao-dien-mobile.PNG'),
      title: "Giao Diện Mobile",
      description: "Giao diện trên thiết bị di động thân thiện và dễ dàng quản lý."
    }
  ];

  // Auto-slide functionality
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextGallerySlide = () => {
    setCurrentGallerySlide((prev) => (prev + 1) % galleryImages.length);
  };

  const prevGallerySlide = () => {
    setCurrentGallerySlide((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Monitor className="h-8 w-8 mr-2" />
              <span className="text-2xl font-semibold">VPS Windows</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              VPS Windows
            </h1>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Mua VPS Windows với 100% lưu trữ SSD NVMe và mức giá cạnh tranh chỉ từ 115.000đ/tháng tại đây. Tận hưởng hiệu suất cao và độ ổn định vượt trội với các gói VPS Windows chất lượng cao của chúng tôi.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center bg-blue-500 bg-opacity-50 px-4 py-2 rounded-full">
                <Zap className="h-4 w-4 mr-2" />
                Hiệu suất cao
              </div>
              <div className="flex items-center bg-blue-500 bg-opacity-50 px-4 py-2 rounded-full">
                <Shield className="h-4 w-4 mr-2" />
                Bảo mật Windows
              </div>
              <div className="flex items-center bg-blue-500 bg-opacity-50 px-4 py-2 rounded-full">
                <Server className="h-4 w-4 mr-2" />
                Remote Desktop
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">BẢNG GIÁ CLOUD WINDOWS VPS</h2>
            </div>
          {/* Alert */}
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-8 rounded">
            <p className="font-bold">Ưu đãi hấp dẫn giảm ngay 40% khi gia hạn 3 năm.</p>
            <p>Giảm 20% khi gia hạn 1 năm, giảm 10% khi gia hạn 6 tháng, giảm 5% khi gia hạn 3 tháng.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.slice(0, 3).map((plan, index) => (
              <div 
                key={index}
                className={`bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 ${
                  plan.popular ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                {plan.popular && (
                  <div className="bg-blue-500 text-white text-center py-2 font-semibold">
                    BÁN CHẠY
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-blue-600">{plan.price}</span>
                      <span className="text-gray-600 ml-1"> VNĐ/Tháng</span>
                    </div>
                    <div className="text-sm text-gray-500 line-through">{plan.originalPrice} VNĐ</div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-600">CPU:</span>
                      <span className="font-semibold">{plan.cpu}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">RAM:</span>
                      <span className="font-semibold">{plan.ram}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Storage:</span>
                      <span className="font-semibold">{plan.storage}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Bandwidth:</span>
                      <span className="font-semibold">{plan.bandwidth}</span>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a 
                    href="https://cloud.vmon.vn/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center ${
                      plan.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Đăng Ký
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {plans.slice(3, 6).map((plan, index) => (
              <div 
                key={index}
                className={`bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 ${
                  plan.popular ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                {plan.popular && (
                  <div className="bg-blue-500 text-white text-center py-2 font-semibold">
                    BÁN CHẠY
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-blue-600">{plan.price}</span>
                      <span className="text-gray-600 ml-1"> VNĐ/Tháng</span>
                    </div>
                    <div className="text-sm text-gray-500 line-through">{plan.originalPrice} VNĐ</div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-600">CPU:</span>
                      <span className="font-semibold">{plan.cpu}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">RAM:</span>
                      <span className="font-semibold">{plan.ram}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Storage:</span>
                      <span className="font-semibold">{plan.storage}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Bandwidth:</span>
                      <span className="font-semibold">{plan.bandwidth}</span>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a 
                    href="https://cloud.vmon.vn/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center ${
                      plan.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Đăng Ký
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Pricing */}
          <div className="mt-12 bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Bảng Giá Đăng Ký Thêm CPU, RAM, Ổ Cứng, IP
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {additionalPricing.map((item, index) => (
                <div key={index} className="text-center p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-center mb-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    <span className="font-semibold text-gray-900">{item.item}</span>
                  </div>
                  <p className="text-blue-600 font-medium">{item.price}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              NHỮNG ƯU ĐIỂM VƯỢT TRỘI KHI Thuê VPS WINDOWS TẠI VMon Cloud
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <advantage.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{advantage.title}</h3>
                <p className="text-gray-600 text-sm">{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              LỢI ÍCH KHI THUÊ VPS WINDOWS GIÁ RẺ TẠI VMON CLOUD
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-lg text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Feedback Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">KHÁCH HÀNG NÓI GÌ VỀ CHÚNG TÔI?</h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              Ý kiến của khách hàng rất quan trọng đối với chúng tôi. Chúng tôi luôn lắng nghe và tiếp thu phản hồi để cải thiện chất lượng sản phẩm và dịch vụ. Điều này giúp chúng tôi đáp ứng tốt hơn nhu cầu và mong muốn của khách hàng.
            </p>
          </div>

          {/* Testimonial Slider */}
          <div className="relative max-w-4xl mx-auto">
            {/* Main Slider */}
            <div className="relative overflow-hidden rounded-lg shadow-lg bg-white">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="w-full flex-shrink-0 flex justify-center items-center p-4">
                    <img
                      src={testimonial}
                      alt={`Customer Feedback ${index + 1}`}
                      className="max-w-full max-h-96 object-contain rounded-lg shadow-sm"
                    />
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
              >
                <ChevronLeft className="h-6 w-6 text-gray-700" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
              >
                <ChevronRight className="h-6 w-6 text-gray-700" />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentSlide 
                      ? 'bg-blue-600 scale-110' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Thư Viện Hình Ảnh</h2>
          <p className="text-lg text-gray-600 mb-12 text-center">
            Việc quản lý tài khoản, tạo mới và giám sát VPS trở nên dễ dàng hơn bao giờ hết với trang Panel hiện đại, thân thiện và dễ sử dụng.
          </p>
          
          {/* Gallery Slider */}
          <div className="relative max-w-4xl mx-auto">
            <div className="overflow-hidden rounded-lg bg-gray-100">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentGallerySlide * 100}%)` }}
              >
                {galleryImages.map((image, index) => (
                  <div key={index} className="w-full flex-shrink-0 p-8">
                    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                      <img
                        src={image.src}
                        alt={image.title}
                        className="w-full h-64 object-contain bg-gray-50"
                      />
                      <div className="p-6 text-center">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{image.title}</h3>
                        <p className="text-gray-600">{image.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <button
              onClick={prevGallerySlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all"
            >
              <ChevronLeft className="h-6 w-6 text-gray-600" />
            </button>
            <button
              onClick={nextGallerySlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all"
            >
              <ChevronRight className="h-6 w-6 text-gray-600" />
            </button>
            
            {/* Dots Indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentGallerySlide(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentGallerySlide ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Specifications Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Thông Số Kỹ Thuật</h2>
            <p className="text-lg text-gray-600">Chi tiết cấu hình Windows Server</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {specifications.map((spec, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">{spec.label}</div>
                <div className="font-semibold text-gray-900">{spec.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              NHỮNG CÂU HỎI THƯỜNG GẶP VỀ MÁY CHỦ ẢO WINDOWS
            </h2>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <details key={index} className="bg-white rounded-lg p-6 shadow">
                <summary className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600">
                  {item.question}
                </summary>
                <p className="mt-4 text-gray-600">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Cùng nhau, Trực tuyến.
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Chúng tôi ở đây giúp các bạn có thể làm việc online một cách hiệu quả nhất.
          </p>
          <a 
            href="https://cloud.vmon.vn/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors inline-block"
          >
            Bắt đầu ngay
          </a>
        </div>
      </section>
    </div>
  );
};

export default VPSWindows;